/**
 * Read-only row of 1–5 circles matching the interactive audit (`RatingRow`).
 *
 * `value == null` (`null` or `undefined`) means the statement was marked N/A
 * — this happens when a decoded `audit_scores` array position is a real
 * `nil` (task #390; see QuarterlyCheckIn::NA_SENTINEL /
 * WheelSnapshot#audit_scores_for_history_json, which now preserves `nil`
 * instead of coercing it to `0`). We render an explicit "N/A" chip here
 * instead of falling into the numeric branch, because `Math.round(Number(0))
 * || 1` previously misread a skip as a selected "1" (strongly disagree).
 * The chip's footprint (h-9, similar width) matches the 5-circle row so the
 * answers list stays visually aligned whether a statement was answered or
 * skipped.
 */
export function RatingRowDisplay({ value, ariaLabel }) {
  if (value == null) {
    return (
      <div
        role="img"
        aria-label={
          ariaLabel ? `${ariaLabel}: Not applicable` : "Not applicable"
        }
        className="flex h-9 w-[212px] shrink-0 items-center justify-center rounded-md border border-dashed border-zinc-400 bg-zinc-700 text-xs font-semibold uppercase tracking-wide text-white"
      >
        N/A
      </div>
    )
  }

  const values = [1, 2, 3, 4, 5]
  const n = Math.min(5, Math.max(1, Math.round(Number(value)) || 1))

  return (
    <div
      role="img"
      aria-label={
        ariaLabel ? `${ariaLabel}: rating ${n} of 5` : `Rating ${n} of 5`
      }
      className="flex shrink-0 items-center gap-2"
    >
      {values.map((v) => {
        const selected = n === v
        return (
          <div
            key={v}
            className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold ${
              selected
                ? "bg-kf-red text-white"
                : "border border-zinc-200 bg-white text-zinc-400"
            }`}
          >
            {v}
          </div>
        )
      })}
    </div>
  )
}

// `allowNA` renders a 6th "N/A" control inside the same radiogroup for
// statements the audit allows a member to opt out of (see
// `AUDIT_OPTIONAL` in `audit-questions.js`). The N/A value is represented
// as the literal string `"na"` in state — this is a wire sentinel
// (task #390): it must survive unchanged through onChange all the way to
// submit, because Rails' deep_munge strips a raw `null` out of a
// submitted scores array before any server code runs. Do not convert
// `"na"` to `null`/`undefined` anywhere in this component.
export function RatingRow({ value, onChange, ariaLabel, allowNA = false }) {
  const values = [1, 2, 3, 4, 5]
  // Treat "na" (and null/undefined) as "unset" for arrow-key math — only a
  // real 1-5 number should participate in +/-1 arithmetic. Without this,
  // `"na" + 1` produces the string "na1" (NaN once coerced by Math.min/max).
  const numericValue = typeof value === "number" ? value : null
  const handleArrowKey = (e) => {
    if (e.key === "ArrowRight" || e.key === "ArrowUp") {
      e.preventDefault()
      onChange(Math.min(5, (numericValue || 0) + 1))
    } else if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
      e.preventDefault()
      onChange(Math.max(1, (numericValue || 6) - 1))
    }
  }

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className="flex items-center gap-2"
    >
      {allowNA && (
        <button
          type="button"
          role="radio"
          aria-checked={value === "na"}
          aria-label={
            ariaLabel ? `${ariaLabel}: Not applicable` : "Not applicable"
          }
          // Roving tabindex: when N/A is selected, none of the numeric
          // buttons below are ever `selected` (their `selected` check is
          // `value === v` for numeric v), so without this branch the whole
          // group loses its one Tab stop and becomes keyboard-unreachable.
          tabIndex={value === "na" ? 0 : -1}
          onClick={() => onChange("na")}
          onKeyDown={handleArrowKey}
          className={`h-9 px-3 rounded-md text-xs font-semibold uppercase tracking-wide transition-all ${
            value === "na"
              ? "bg-zinc-700 border border-zinc-700 text-white"
              : "bg-white border border-dashed border-zinc-300 text-zinc-500 hover:border-zinc-500 hover:text-zinc-700"
          }`}
        >
          N/A
        </button>
      )}
      {values.map((v) => {
        const selected = value === v
        return (
          <button
            key={v}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected || (!value && v === 1) ? 0 : -1}
            onClick={() => onChange(v)}
            onKeyDown={handleArrowKey}
            className={`w-9 h-9 rounded-full text-sm font-semibold transition-all ${
              selected
                ? "bg-kf-red text-white"
                : "bg-white border border-zinc-300 text-zinc-600 hover:border-kf-red hover:text-kf-red"
            }`}
          >
            {v}
          </button>
        )
      })}
    </div>
  )
}
